/**
 * Runtime configuration, fetched straight from the LMS.
 *
 * Nothing about a tenant — name, domains, colours, copy, features — is in the
 * bundle. The tenancy app resolves it from the request hostname and merges the
 * shipped catalogues over the tenant's database values, so this is one call.
 *
 * The only build-time value in the whole app is VITE_LMS_ORIGIN, which is
 * per-environment and not per-tenant.
 */

import { openedx, ApiError } from './openedx';

export type SurfaceMode = 'own' | 'embedded' | 'external' | 'off';
export type AppMode = 'platform' | 'tenant';

export interface RuntimeConfig {
  mode: AppMode;
  tenant: {
    slug: string;
    name: string;
    status: string;
    orgs: string[];
    /**
     * Courses licensed in from an org this tenant does not own.
     *
     * Course keys, not orgs. Part of the config because the catalogue cannot be read
     * without them: the platform's list endpoint is scoped by `course_org_filter` server
     * side, so these are reachable only by key. See `catalogue.ts`.
     */
    licensedCourses: string[];
  };
  domains: { site: string; studio: string | null; preview: string | null };
  auth: {
    primary: 'oidc' | 'native';
    oidcEntrypoint: string;
    allowNativeLogin: boolean;
    allowNativeRegistration: boolean;
  };
  branding: {
    colors: Record<string, string>;
    typography: Record<string, string>;
    radius: string;
    logo?: string;
    favicon?: string;
  };
  copy: Record<string, string>;
  features: Record<string, boolean>;
  surfaces: Record<string, SurfaceMode>;
  locale: { default: string; available: string[] };
  /** Structured content, resolved from platform defaults + this tenant's overrides. */
  footer: FooterConfig;
  landing: LandingConfig;
}

export interface FooterLink {
  label: string;
  href: string;
}

export interface FooterConfig {
  columns?: { heading: string; links: FooterLink[] }[];
  legal?: FooterLink[];
  social?: { icon: string; href: string; label?: string }[];
}

export interface LandingConfig {
  values?: { icon?: string; title: string; body: string }[];
  stats?: { value: string; label: string }[];
}

let cached: RuntimeConfig | null = null;

export async function loadConfig(): Promise<RuntimeConfig> {
  if (cached) return cached;

  // The hostname is passed explicitly: the request goes TO the LMS, so its own Host
  // header is the LMS's, not ours. The tenancy app cannot infer us from it.
  const host = window.location.hostname;

  try {
    cached = await openedx.get<RuntimeConfig>(
      `/api/tenant/v1/tenants/by-host/${encodeURIComponent(host)}`,
    );
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      throw new Error(`No academy is registered for ${host} yet.`);
    }
    throw error;
  }

  applyBranding(cached);
  return cached;
}

export function getConfig(): RuntimeConfig {
  if (!cached) throw new Error('loadConfig() must resolve before getConfig()');
  return cached;
}

export function isEnabled(flag: string): boolean {
  return getConfig().features[flag] === true;
}

export function surfaceMode(surface: string): SurfaceMode {
  return getConfig().surfaces[surface] ?? 'off';
}

export function text(key: string, fallback = ''): string {
  return getConfig().copy[key] ?? fallback;
}

/**
 * Project tenant branding onto CSS custom properties.
 *
 * This is what replaces per-tenant theme builds: one bundle, one stylesheet, palette
 * as data. Adding a themeable value means adding a token to config/platform.yaml.
 */
function applyBranding(config: RuntimeConfig): void {
  const root = document.documentElement;
  root.dataset.app = config.mode;

  if (config.mode === 'platform') {
    // The console keeps the fixed palette from tokens.css. Applying tenant colours
    // here would remove the operator's only cue that they are editing every tenant.
    document.title = 'Platform console';
    return;
  }

  // Only BRAND colours are applied. `surface` and `ink` describe the theme, not the
  // brand: writing them onto :root overrode the stylesheet and produced near-black
  // text on a near-black header. Existing tenants still have them stored from an
  // earlier version, so they are filtered here rather than trusted.
  const BRAND_KEYS = new Set(['primary', 'primary-hover', 'secondary', 'accent']);
  for (const [name, value] of Object.entries(config.branding.colors ?? {})) {
    if (BRAND_KEYS.has(name)) root.style.setProperty(`--color-${name}`, value);
  }
  for (const [name, value] of Object.entries(config.branding.typography ?? {})) {
    root.style.setProperty(`--font-${name}`, value);
  }
  if (config.branding.radius) root.style.setProperty('--radius', config.branding.radius);

  document.title = config.tenant.name;
  root.lang = config.locale?.default ?? 'en';
}
